/**
 * @fileoverview Public booking page (Spec 08 Phase 3): /book/:token, and the
 * marketplace's program-scoped variant /programs/:programId/book/:userId.
 *
 * No Hans account needed — the token in the URL (or the program membership)
 * is the authorization. Shows the instructor's first name and their derived
 * openings (grouped by day, rendered in the visitor's local time from the
 * absolute start_at instants), then runs the hold → contact form → confirm
 * flow. Times are claimed with a 10-minute hold so two visitors can't take
 * the same opening. Both routes hit the same backend internals; only the
 * URL base differs.
 *
 * Uses a bare axios instance: the shared api client's 401 interceptor
 * redirects to the instructor login, which must never happen on a public page.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LESSON_TYPE_LABELS } from "@/lib/schedule";
import BACKEND_URL from "@/lib/backendUrl";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const detailMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  return fallback;
};

/** Group openings by their visitor-local calendar day. */
const groupByLocalDay = (openings) => {
  const groups = new Map();
  for (const opening of openings) {
    const local = new Date(opening.start_at);
    const key = local.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(opening);
  }
  return [...groups.entries()];
};

const localTime = (startAt) =>
  new Date(startAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const BookingPage = () => {
  const { token, programId, userId } = useParams();
  // Token flow: /book/:token → /api/booking/{token}. Program flow:
  // /programs/:programId/book/:userId → the program-scoped endpoints.
  const base = token ? `/booking/${token}` : `/programs/${programId}/instructors/${userId}/booking`;
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // opening being booked
  const [hold, setHold] = useState(null); // {booking_id, hold_expires_at}
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [flash, setFlash] = useState(null); // transient inline error
  const [lessonType, setLessonType] = useState(null); // visitor's pick from the offered formats
  const [photoBroken, setPhotoBroken] = useState(false);

  const fetchPage = useCallback(async () => {
    try {
      const res = await api.get(base);
      setPageData(res.data);
      // Preselect the first offered format so one-format instructors need no click.
      const offered = res.data?.lesson_types || [];
      setLessonType((prev) => (prev && offered.includes(prev) ? prev : offered[0] || null));
    } catch (err) {
      setError(
        err.response?.status === 404
          ? token
            ? "This booking link is no longer active. Ask your instructor for a new one."
            : "This instructor isn't taking bookings through this program right now."
          : "Could not load the booking page — try again in a moment."
      );
    }
  }, [base, token]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const dayGroups = useMemo(() => groupByLocalDay(pageData?.openings || []), [pageData]);

  const pickOpening = async (opening) => {
    setFlash(null);
    try {
      const res = await api.post(`${base}/hold`, {
        date: opening.date,
        time_local: opening.time_local,
        ...(lessonType ? { lesson_type: lessonType } : {}),
      });
      setSelected(opening);
      setHold(res.data);
    } catch (err) {
      setFlash(detailMessage(err, "That time was just taken — pick another opening."));
      fetchPage();
    }
  };

  const confirm = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setFlash("Add your name and email so the instructor knows who's booking.");
      return;
    }
    setSubmitting(true);
    setFlash(null);
    try {
      await api.post(`${base}/confirm`, {
        booking_id: hold.booking_id,
        name: form.name.trim(),
        email: form.email.trim(),
        note: form.note.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setFlash(detailMessage(err, "Could not submit — your hold may have expired. Pick a time again."));
      setSelected(null);
      setHold(null);
      fetchPage();
    } finally {
      setSubmitting(false);
    }
  };

  const shellClass = "min-h-screen px-6 py-14";
  const shellStyle = {
    background:
      "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #08080a",
  };

  if (error) {
    return (
      <div
        className={shellClass}
        style={shellStyle}
        data-theme="dark"
      >
        <div className="mx-auto max-w-md text-center">
          <p className="font-sans text-base text-zinc-400" data-testid="booking-error">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div
        className={shellClass}
        style={shellStyle}
        data-theme="dark"
      >
        <div className="mx-auto max-w-md text-center" data-testid="booking-done">
          <div className="mb-4 text-4xl">🎵</div>
          <h1 className="font-serif text-3xl font-normal text-white">Request sent</h1>
          <p className="mt-3 font-sans text-sm leading-relaxed text-zinc-400">
            {pageData?.instructor_name} will confirm your lesson
            {selected ? ` for ${localTime(selected.start_at)} on ${new Date(selected.start_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}` : ""}.
            You&apos;ll hear back by email or through your student portal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-theme="dark"
    >
      <div className="mx-auto max-w-xl" data-testid="booking-page">
        <div className="mb-8 text-center">
          {/* Spec 12 WP3: owner-org brand on the program-page booking header. */}
          {pageData?.branding?.logo_url && (
            <img
              src={pageData.branding.logo_url}
              alt=""
              data-testid="booking-org-logo"
              className="mx-auto mb-2 h-7 w-7 rounded object-cover"
            />
          )}
          <p
            className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: pageData?.branding?.accent_color || "#f4cf08" }}
          >
            Book a lesson
          </p>
          {pageData?.photo_url && !photoBroken && (
            <img
              src={pageData.photo_url}
              alt={`${pageData.instructor_name}'s photo`}
              onError={() => setPhotoBroken(true)}
              data-testid="booking-photo"
              className="mx-auto mt-4 h-24 w-24 rounded-full border border-white/10 object-cover"
            />
          )}
          <h1 className="mt-2 font-serif text-[34px] font-normal tracking-[-0.01em] text-white">
            {pageData ? `Lessons with ${pageData.instructor_name}` : "Loading…"}
          </h1>
          {pageData?.program?.title && (
            <p className="mt-1 font-sans text-[13px] text-zinc-500" data-testid="booking-program">
              Part of {pageData.program.title}
              {pageData?.branding?.org_name ? ` · ${pageData.branding.org_name}` : ""}
            </p>
          )}
          {pageData?.bio && (
            <p className="mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-zinc-400" data-testid="booking-bio">
              {pageData.bio}
            </p>
          )}
          <p className="mt-2 font-sans text-sm text-zinc-500">
            Times are shown in your local time zone. Picking one holds it for 10 minutes.
          </p>
        </div>

        {pageData?.lesson_types?.length > 0 && !selected && (
          <div className="mb-6 text-center" data-testid="lesson-type-picker">
            <p className="mb-2.5 font-sans text-[13px] font-semibold uppercase tracking-wide text-zinc-500">
              Lesson type
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {pageData.lesson_types.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setLessonType(type)}
                  data-testid={`lesson-type-${type}`}
                  aria-pressed={lessonType === type}
                  className={[
                    "rounded-full border px-4 py-2 font-sans text-[13px] transition-colors",
                    lessonType === type
                      ? "border-[#f4cf08] bg-[rgba(244,207,8,0.12)] text-white"
                      : "border-white/10 bg-zinc-900/80 text-zinc-400 hover:text-white",
                  ].join(" ")}
                >
                  {LESSON_TYPE_LABELS[type] || type}
                </button>
              ))}
            </div>
          </div>
        )}

        {flash && (
          <div className="mb-5 rounded-xl border border-orange-500/20 bg-orange-500/10 px-4 py-3 font-sans text-sm text-orange-300" data-testid="booking-flash">
            {flash}
          </div>
        )}

        {selected && hold ? (
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6" data-testid="booking-form">
            <p className="font-sans text-sm text-zinc-300">
              Booking{" "}
              <span className="font-semibold text-white">
                {localTime(selected.start_at)},{" "}
                {new Date(selected.start_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </span>{" "}
              · {selected.duration_minutes} min
              {lessonType ? ` · ${LESSON_TYPE_LABELS[lessonType] || lessonType}` : ""}
            </p>
            <div className="mt-4 space-y-3">
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your name (or your child's)"
                data-testid="booking-name"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-sans text-sm text-white placeholder:text-zinc-600"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email"
                data-testid="booking-email"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-sans text-sm text-white placeholder:text-zinc-600"
              />
              <textarea
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Anything the instructor should know? (optional)"
                rows={3}
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-sans text-sm text-white placeholder:text-zinc-600"
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={confirm}
                disabled={submitting}
                data-testid="booking-submit"
                className="rounded-full bg-[#f4cf08] px-6 py-2.5 font-sans text-sm font-semibold text-black disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Request this time"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setHold(null);
                  fetchPage();
                }}
                className="font-sans text-sm text-zinc-500 underline"
              >
                Pick a different time
              </button>
            </div>
            <p className="mt-4 font-sans text-[12px] text-zinc-600">
              Your request isn&apos;t final until {pageData?.instructor_name} confirms it.
            </p>
          </div>
        ) : pageData && dayGroups.length === 0 ? (
          <p className="text-center font-sans text-sm text-zinc-500" data-testid="booking-empty">
            No open times right now — check back soon or contact the instructor directly.
          </p>
        ) : (
          <div className="space-y-6">
            {dayGroups.map(([day, openings]) => (
              <div key={day}>
                <h2 className="mb-2.5 font-sans text-[13px] font-semibold uppercase tracking-wide text-zinc-500">
                  {day}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {openings.map((opening) => (
                    <button
                      key={`${opening.date}-${opening.time_local}`}
                      type="button"
                      onClick={() => pickOpening(opening)}
                      data-testid={`opening-${opening.date}-${opening.time_local}`}
                      className="rounded-full border border-white/10 bg-zinc-900/80 px-4 py-2 font-mono text-[13px] text-zinc-200 transition-colors hover:border-[rgba(244,207,8,0.4)] hover:text-white"
                    >
                      {localTime(opening.start_at)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {pageData && pageData.powered_by_hans !== false && pageData.branding?.powered_by_hans !== false && (
          <p className="mt-10 text-center font-sans text-[11px] text-zinc-600" data-testid="powered-by-hans">
            Powered by Hans
          </p>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
